// Portal recipes of a carrier and their shadow-mode record (backend plan C3):
// the declarative recipe replayed next to the Selenium script, never
// submitting, so the agreement rate and locator drift are visible before a
// recipe is published. A test run stops before the submit step.
// API (proxied to the mail service): GET /carrier-recipes?carrier=,
// GET /carrier-recipes/shadow-runs?carrier=&limit=20, POST
// /carrier-recipes/{id}/test-run; the studio (RecipeStudio) records new ones.

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight, CircleCheck, CircleHelp, CircleX, ExternalLink, FlaskConical, Pencil, Play, RefreshCw, Sparkles, Video,
} from 'lucide-react';
import { Badge, Banner, Card, EmptyState, Meter } from '../design/ui';
import RecipeStudio from './RecipeStudio';
import { formatShortDateTime } from '../utils/dates';
import './CarrierRecipes.css';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const pct = (v) => (typeof v === 'number' ? `${Math.round(v * 100)} %` : '—');

export const RECIPE_STATUS_TONE = { published: 'success', test: 'warning', draft: undefined, retired: undefined };
const RUN_TONE = { success: 'success', stopped_before_submit: 'success', failed: 'danger', runner_unavailable: 'danger' };
const STEP_TONE = { ok: 'success', failed: 'danger' };
const STEP_ICON = { ok: CircleCheck, failed: CircleX };

// One test run: status, the step reached, each step's outcome, captures.
export const RecipeRunResult = ({ result, t }) => {
  const run = result?.run;
  if (!run) return null;
  return (
    <div className={`crc-v2__result crc-v2__result--${RUN_TONE[run.status] || 'neutral'}`}>
      <div className="crc-v2__result-head">
        <Badge tone={RUN_TONE[run.status]} dot>{t(`carrierRecipes.runStatus.${run.status}`, run.status)}</Badge>
        <span className="u-muted">
          {[run.reached_step, run.duration_ms != null && `${Math.round((run.duration_ms || 0) / 1000)} s`].filter(Boolean).join(' · ')}
        </span>
      </div>
      {run.error && <Banner tone="danger">{run.error}</Banner>}
      <div className="crc-v2__steps">
        {(run.steps || []).map((step) => {
          const Icon = STEP_ICON[step.status] || CircleHelp;
          return (
            <span key={step.id} className="crc-v2__step" title={[step.strategy, step.selector, step.error].filter(Boolean).join(' · ')}>
              <Badge tone={STEP_TONE[step.status]} icon={Icon}>
                {step.id}
                {step.strategy === 'llm_repair' && (
                  <Sparkles className="crc-v2__repair" aria-label={t('carriersUi.recipes.repaired', 'réparé par le modèle')} />
                )}
              </Badge>
            </span>
          );
        })}
      </div>
      {(run.drift || []).length > 0 && (
        <p className="u-help">{t('carrierRecipes.driftNote', 'Localisateurs réparés par le modèle : {{steps}}', { steps: run.drift.join(', ') })}</p>
      )}
      {(result.captures || []).length > 0 && (
        <div className="crc-v2__captures">
          {result.captures.slice(0, 8).map((capture, index) => (capture.url ? (
            // eslint-disable-next-line react/no-array-index-key
            <a key={index} className="u-link" href={capture.url} target="_blank" rel="noopener noreferrer">
              {capture.step || t('carriersUi.recipes.capture', 'capture {{n}}', { n: index + 1 })}
              <ExternalLink aria-hidden="true" />
            </a>
          ) : null))}
        </div>
      )}
    </div>
  );
};

const CarrierRecipes = ({ carrierName, isAdmin = true }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language || 'fr';
  const [recipes, setRecipes] = useState(null);
  const [runnerAvailable, setRunnerAvailable] = useState(null);
  const [shadow, setShadow] = useState(null);
  const [error, setError] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [tracking, setTracking] = useState('');
  const [studio, setStudio] = useState(null); // null | { recipe } (recipe null = record a new one)

  const load = useCallback(async () => {
    if (!carrierName) return;
    setError(null);
    try {
      const [recipesRes, shadowRes] = await Promise.all([
        axios.get(`/api/logistics/carrier-recipes?carrier=${encodeURIComponent(carrierName)}`, authHeaders()),
        axios.get(`/api/logistics/carrier-recipes/shadow-runs?carrier=${encodeURIComponent(carrierName)}&limit=20`, authHeaders()),
      ]);
      setRecipes(recipesRes.data.recipes || []);
      setRunnerAvailable(recipesRes.data.runner_available);
      setShadow(shadowRes.data);
    } catch (e) {
      setRecipes([]);
      setShadow(null);
      setError(t('carrierRecipes.loadError', 'Impossible de charger les recettes.'));
    }
  }, [carrierName, t]);

  useEffect(() => { load(); }, [load]);

  const testRun = async (recipe) => {
    setTesting(recipe.id);
    setTestResult(null);
    setError(null);
    try {
      const variables = tracking ? { tracking_number: tracking.trim() } : {};
      const response = await axios.post(`/api/logistics/carrier-recipes/${encodeURIComponent(recipe.id)}/test-run`, { variables }, authHeaders());
      setTestResult({ recipe: recipe.id, ...response.data });
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('carrierRecipes.testError', 'La run de test a échoué.'));
    } finally {
      setTesting(null);
    }
  };

  const pairs = shadow?.summary?.pairs || [];
  const runs = shadow?.runs || [];
  const enabled = (shadow?.enabled_for || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  const shadowOn = enabled.includes('*') || enabled.includes((carrierName || '').toLowerCase());
  const visibleRecipes = (recipes || []).filter((r) => r.operation !== 'login');

  // The studio (plan C5) records a new recipe even when the carrier has none yet.
  return (
    <Card
      icon={FlaskConical}
      title={t('carrierRecipes.title', 'Recettes de portail')}
      badge={(
        <>
          {shadowOn
            ? <Badge tone="success" dot>{t('carrierRecipes.shadowOn', 'mode ombre actif')}</Badge>
            : <Badge dot>{t('carrierRecipes.shadowOff', 'mode ombre inactif')}</Badge>}
          {runnerAvailable === false && <Badge tone="warning" dot>{t('carrierRecipes.runnerUnavailable', 'runner indisponible')}</Badge>}
        </>
      )}
      description={t('carrierRecipes.intro', 'Une recette décrit les étapes du portail par cibles sémantiques ; en mode ombre elle est rejouée à côté du script sans jamais soumettre, et l’accord entre les deux est mesuré avant publication.')}
      actions={(
        <>
          <button
            type="button"
            className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
            onClick={load}
            title={t('carrierRecipes.refresh', 'Actualiser')}
            aria-label={t('carrierRecipes.refresh', 'Actualiser')}
          >
            <RefreshCw aria-hidden="true" />
          </button>
          {isAdmin && (
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setStudio({ recipe: null })}>
              <Video aria-hidden="true" />
              {t('carrierRecipes.record', 'Enregistrer une recette')}
            </button>
          )}
        </>
      )}
      flush
    >
      {error && <div className="crc-v2__pad"><Banner tone="danger">{error}</Banner></div>}
      {recipes === null && (
        <div className="crc-v2__skeleton" aria-busy="true" aria-label={t('carrierRecipes.loading', 'Chargement…')}>
          <span className="u-skeleton" />
          <span className="u-skeleton" />
        </div>
      )}
      {recipes && visibleRecipes.length > 0 && (
        <div className="u-table-scroll">
          <table className="u-table crc-v2__table">
            <thead>
              <tr>
                <th style={{ width: '26%' }}>{t('carrierRecipes.recipe', 'Recette')}</th>
                <th style={{ width: '12%' }}>{t('carrierRecipes.operation', 'Opération')}</th>
                <th style={{ width: '13%' }}>{t('carrierRecipes.status', 'Statut')}</th>
                <th style={{ width: '20%' }}>{t('carrierRecipes.agreement', 'Accord (14 j)')}</th>
                <th style={{ width: '10%' }}>{t('carrierRecipes.drift', 'Dérive')}</th>
                <th style={{ width: '19%' }} aria-label={t('carriersUi.actions', 'Actions')} />
              </tr>
            </thead>
            <tbody>
              {visibleRecipes.map((r) => {
                const pair = pairs.find((p) => p.operation === r.operation);
                const decided = pair ? pair.agreed + pair.disagreed : 0;
                return (
                  <tr key={`${r.source}-${r.db_id || r.id}`}>
                    <td title={r.description || r.id}>
                      <span className="crc-v2__id">{r.id}</span>
                      <span className="u-cell-sub">
                        v{r.version} · {r.scope === 'tenant' ? t('carrierRecipes.scopeTenant', 'compte') : t('carrierRecipes.scopeCatalog', 'catalogue')}
                      </span>
                    </td>
                    <td>{t(`carrierRecipes.op.${r.operation}`, r.operation)}</td>
                    <td><Badge tone={RECIPE_STATUS_TONE[r.status]} dot>{t(`carrierRecipes.state.${r.status}`, r.status)}</Badge></td>
                    <td>
                      {pair && typeof pair.agreement_rate === 'number' ? (
                        <span className="crc-v2__agreement" title={t('carriersUi.recipes.agreementTitle', '{{agreed}} accords sur {{total}} runs décidées', { agreed: pair.agreed, total: decided })}>
                          <span className="u-num">{pct(pair.agreement_rate)}</span>
                          <Meter value={Math.round(pair.agreement_rate * 100)} tone={pair.agreement_rate >= 0.9 ? 'success' : 'warning'} label={t('carrierRecipes.agreement', 'Accord (14 j)')} />
                          <span className="u-muted u-num">{pair.agreed}/{decided}</span>
                        </span>
                      ) : <span className="u-muted">—</span>}
                    </td>
                    <td>
                      {pair
                        ? <span className="u-num">{pair.drift_runs}/{pair.runs}</span>
                        : (r.locator_cache_size
                          ? <span className="u-muted">{t('carriersUi.recipes.learned', '{{n}} appris', { n: r.locator_cache_size })}</span>
                          : <span className="u-muted">—</span>)}
                    </td>
                    <td>
                      <span className="crc-v2__actions">
                        {isAdmin && r.source === 'db' && (
                          <button
                            type="button"
                            className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                            onClick={() => setStudio({ recipe: r })}
                            title={t('carrierRecipes.openStudio', 'Ouvrir dans le studio')}
                            aria-label={t('carrierRecipes.openStudio', 'Ouvrir dans le studio')}
                          >
                            <Pencil aria-hidden="true" />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            type="button"
                            className="u-btn u-btn--secondary u-btn--sm"
                            disabled={testing === r.id || runnerAvailable === false}
                            onClick={() => testRun(r)}
                          >
                            <Play aria-hidden="true" />
                            {testing === r.id ? t('carrierRecipes.testing', 'Test en cours…') : t('carrierRecipes.testRun', 'Run de test')}
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {isAdmin && recipes && visibleRecipes.length > 0 && (
        <div className="crc-v2__test">
          <label className="u-label" htmlFor="crc-tracking">{t('carrierRecipes.trackingForTest', 'Numéro de suivi pour la run de test')}</label>
          <input id="crc-tracking" className="u-input" value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="6A…" />
          <span className="u-help">{t('carriersUi.recipes.testHelp', 'Une run de test s’arrête toujours avant l’envoi : rien n’est déposé chez le transporteur.')}</span>
        </div>
      )}
      {testResult && testResult.run && <div className="crc-v2__pad"><RecipeRunResult result={testResult} t={t} /></div>}
      {recipes && visibleRecipes.length === 0 && !error && (
        <EmptyState
          icon={FlaskConical}
          title={t('carriersUi.recipes.emptyTitle', 'Aucune recette pour ce transporteur')}
          action={isAdmin && (
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setStudio({ recipe: null })}>
              <Video aria-hidden="true" />
              {t('carrierRecipes.record', 'Enregistrer une recette')}
            </button>
          )}
        >
          {t('carriersUi.recipes.emptyHint', 'Enregistrez le parcours du portail une fois dans le studio : la recette est testée en mode ombre avant d’être publiée.')}
        </EmptyState>
      )}
      {runs.length > 0 && (
        <details className="u-disclosure crc-v2__runs">
          <summary>
            <ChevronRight aria-hidden="true" />
            {t('carrierRecipes.recentRuns', 'Dernières runs ombre ({{count}})', { count: runs.length })}
          </summary>
          <div className="u-table-scroll">
            <table className="u-table crc-v2__runs-table">
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>{t('carrierRecipes.when', 'Quand')}</th>
                  <th style={{ width: '14%' }}>{t('carrierRecipes.operation', 'Opération')}</th>
                  <th style={{ width: '22%' }}>{t('carrierRecipes.script', 'Script')}</th>
                  <th style={{ width: '22%' }}>{t('carrierRecipes.recipe', 'Recette')}</th>
                  <th style={{ width: '22%' }}>{t('carrierRecipes.verdict', 'Verdict')}</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const reason = t(`carrierRecipes.reason.${r.reason}`, (r.reason || '').replace(/_/g, ' '));
                  return (
                    <tr key={r.id}>
                      <td>{formatShortDateTime(r.created_at, lang, { empty: '' })}</td>
                      <td>{t(`carrierRecipes.op.${r.operation}`, r.operation)}</td>
                      <td title={r.selenium?.failure_category || ''}>
                        {r.selenium?.status}
                        {r.selenium?.failure_category && <span className="u-cell-sub">{r.selenium.failure_category}</span>}
                      </td>
                      <td title={(r.recipe?.drift || []).join(', ')}>
                        {t(`carrierRecipes.runStatus.${r.recipe?.status}`, r.recipe?.status)}
                        {(r.recipe?.reached_step || (r.recipe?.drift || []).length > 0) && (
                          <span className="u-cell-sub">
                            {[r.recipe?.reached_step, (r.recipe?.drift || []).length > 0 && t('carriersUi.recipes.drifted', 'localisateur réparé')].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </td>
                      <td title={reason}>
                        {r.agreement === true && <Badge tone="success" icon={CircleCheck}>{t('carriersUi.recipes.agree', 'Accord')}</Badge>}
                        {r.agreement === false && <Badge tone="danger" icon={CircleX}>{t('carriersUi.recipes.disagree', 'Désaccord')}</Badge>}
                        {r.agreement !== true && r.agreement !== false && <Badge icon={CircleHelp}>{t('carriersUi.recipes.undecided', 'Indécis')}</Badge>}
                        {reason && <span className="u-cell-sub">{reason}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      )}
      {studio && <RecipeStudio carrierName={carrierName} initialRecipe={studio.recipe} onClose={() => setStudio(null)} onSaved={load} />}
    </Card>
  );
};

export default CarrierRecipes;
